import { memo, useState, useCallback } from 'react'
import styled from 'styled-components'
import { useTranslation } from 'services/Translation'
import Typography from 'components/common/Typography'
import LoaderView from 'components/common/loaders/LoaderView'
import Separator from 'components/common/Separator'
import { textPrimary, greySecondary } from 'styles/Colors'
import { CS, CS_m } from 'back-end-api'
import { hp } from 'styles/Sizes'
import Copy from 'assets/icons/Copy'
import { ModalContainer, ModalSucceeded, ModalFailed } from '../Common'
import { AdviceStatus } from './common'
import WriteAdvice from './WriteAdvice'
import ConfirmAdvice from './ConfirmAdvice'
import CreateAdviceTemplate from './CreateAdviceTemplate'
import AdviceHistory from './AdviceHistory'

const AdviceContainer = styled.div`
  position: relative;
  width: 100%;
`

const ResizableContainer = styled.div`
  width: 100%;
  min-height: 300px;
  max-height: 600px;
  padding: 12px;
  padding-top: 40px; /* Make room for the copy icon */
  border: 1px solid ${greySecondary};
  border-radius: 8px;
  font-size: 16px;
  font-family: coresanscr45;
  line-height: 1.5;
  resize: vertical;
  background: white;
  color: ${textPrimary};
  overflow-y: auto;
  
  &:focus {
    outline: none;
    border-color: #EB7C0C;
    box-shadow: 0 0 0 2px #EB7C0C20;
  }
  
  /* Style the HTML content */
  p {
    margin: 0 0 0.25em 0;
    &:last-child {
      margin-bottom: 0;
    }
  }
  
  strong, b {
    font-weight: bold;
  }
  
  em, i {
    font-style: italic;
  }
  
  u {
    text-decoration: underline;
  }
  
  ul, ol {
    margin: 0 0 1em 0;
    padding-left: 2em;
  }
  
  ul {
    list-style-type: disc;
  }
  
  ol {
    list-style-type: decimal;
  }
  
  li {
    margin-bottom: 0.5em;
    display: list-item;
  }
  
  h1, h2, h3, h4, h5, h6 {
    margin: 0 0 0.5em 0;
    font-weight: bold;
  }
  
  h1 { font-size: 1.5em; }
  h2 { font-size: 1.3em; }
  h3 { font-size: 1.1em; }
`

const CopyButton = styled.button`
  position: absolute;
  top: 12px;
  right: 12px;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s ease;
  z-index: 1;
  
  &:hover {
    background-color: #EB7C0C20;
  }
  
  &:active {
    background-color: #EB7C0C30;
  }
`

const texts = (getText: (key: string, category: string) => string) => (key: string) =>
  ({
    btnLabelClose: getText('Close', 'Patient_Details_Modal_Advice'),
    btnLabelSent: getText('Advice sent!', 'Patient_Details_Modal_Advice'),
    existingAdviceTitle: getText('Given advice', 'Patient_Details_Modal_Advice'),
  }[key])

const TEMPLATES_PLACEHOLDERS = [
  {
    value: '[patient-firstname]',
    content: 'first_name',
  },
  {
    value: '[patient-lastname]',
    content: 'last_name',
  },
  {
    value: '[patient-age]',
    content: 'age',
  },
  {
    value: '[patient-birthday]',
    content: 'date_of_birth',
  },
  {
    value: '[patient-gender]',
    content: 'gender',
  },
]

interface AdviceFormProps {
  onClose: () => void
  registration: CS_m.ModelsRegistration | null
}

const AdviceForm = ({ onClose, registration }: AdviceFormProps) => {
  const { t } = useTranslation(texts)
  const [advice, setAdvice] = useState('')
  const [title, setTitle] = useState('')
  const [status, setStatus] = useState<AdviceStatus>(
    CS.getSummary(registration)?.advice?.text ? AdviceStatus.AdviceHistory : AdviceStatus.WriteAdvice
  )

  const handleCopyAdvice = useCallback(async () => {
    const adviceText = CS.getSummary(registration)?.advice?.text
    if (adviceText) {
      try {
        // Copy with HTML formatting preserved
        const htmlBlob = new Blob([adviceText], { type: 'text/html' })
        const tempDiv = document.createElement('div')
        tempDiv.innerHTML = adviceText
        const plainText = tempDiv.textContent || tempDiv.innerText || ''
        const textBlob = new Blob([plainText], { type: 'text/plain' })
        await navigator.clipboard.write([
          new window.ClipboardItem({
            'text/html': htmlBlob,
            'text/plain': textBlob,
          }),
        ])
      } catch (err) {
        console.error('Failed to copy advice:', err)
        // Fallback for older browsers
        const tempDiv = document.createElement('div')
        tempDiv.innerHTML = adviceText
        const plainText = tempDiv.textContent || tempDiv.innerText || ''
        
        const textArea = document.createElement('textarea')
        textArea.value = plainText
        document.body.appendChild(textArea)
        textArea.select()
        document.execCommand('copy')
        document.body.removeChild(textArea)
      }
    }
  }, [registration])

  const formatTemplateText = useCallback(
    (rawText: string) => {
      if (!registration) return rawText
      if (!rawText) return ''
      let finalText = `${rawText}`
      TEMPLATES_PLACEHOLDERS.forEach(p => {
        // Use replaceAll to replace all occurrences, not just the first one
        finalText = finalText.replaceAll(
          p.value,
          (registration[p.content as keyof CS_m.ModelsRegistration] as string | null) ?? ''
        )
      })
      return finalText || ''
    },
    [registration]
  )

  switch (status) {
    case AdviceStatus.Error:
      return (
        <ModalFailed
          errorMsg={`Couldn't send advice`}
          containerProps={{ label: t('btnLabelClose'), onClose }}
        />
      )
    case AdviceStatus.Success:
      return (
        <ModalSucceeded
          label={t('btnLabelSent')}
          containerProps={{ label: t('btnLabelClose'), onClose }}
        />
      )
    case AdviceStatus.Loading:
      return <LoaderView onClose={onClose} />
    case AdviceStatus.WriteAdvice:
      return (
        <WriteAdvice
          onClose={onClose}
          formatTemplateText={formatTemplateText}
          setStatus={setStatus}
          advice={advice}
          setAdvice={setAdvice}
          title={title}
          setTitle={setTitle}
        />
      )
    case AdviceStatus.CreateAdviceTemplate:
      return (
        <CreateAdviceTemplate
          onClose={onClose}
          setStatus={setStatus}
          formatTemplateText={formatTemplateText}
          advice={advice}
          setAdvice={setAdvice}
          title={title}
          setTitle={setTitle}
        />
      )
    case AdviceStatus.ConfirmAdvice:
      return (
        <ConfirmAdvice
          onClose={onClose}
          summary={CS.getSummary(registration)}
          setStatus={setStatus}
          advice={advice}
          title={title}
          formatTemplateText={formatTemplateText}
        />
      )
    case AdviceStatus.AdviceHistory:
      return (
        <AdviceHistory
          onClose={onClose}
          registration={registration}
          setStatus={setStatus}
        />
      )
    case AdviceStatus.Review:
      return (
        <ModalContainer onClose={onClose} label={t('btnLabelClose')}>
          <Typography
            type="title"
            className="font-csc65"
            text={t('existingAdviceTitle')}
            color={textPrimary}
            size={18}
            style={undefined}
          />
          <Separator size={hp(3)} />
          <AdviceContainer>
            <CopyButton onClick={handleCopyAdvice} title="Copy advice">
              <Copy className="" color="#EB7C0C" />
            </CopyButton>
            <ResizableContainer
              dangerouslySetInnerHTML={{
                __html: CS.getSummary(registration)?.advice?.text || '<p>No advice available</p>'
              }}
            />
          </AdviceContainer>
        </ModalContainer>
      )
    default:
      console.error('Unhandled Set advice status:', status)
      return null
  }
}

AdviceForm.whyDidYouRender = true

export default memo(AdviceForm)
